import type { BuildContext } from "../types.js";
import { scaleFor } from "../format.js";
import { backgroundGradient } from "../primitives/backgroundGradient.js";
import { brandLogo } from "../primitives/brandLogo.js";
import { cta } from "../primitives/cta.js";
import { card } from "../primitives/card.js";
import { safeArea } from "../primitives/safeArea.js";
import { getLocale, type LocaleCode } from "../../locales/index.js";
import { testimonialSchema, type TestimonialProps } from "../../templates/index.js";
import { colors } from "../../brand/theme.js";
import { escapeHtml } from "../composition.js";
import { assemble } from "./shared.js";

export const TESTIMONIAL_DURATION_SEC = 9;

export function buildTestimonialCardHtml(
  rawProps: unknown,
  ctx: Omit<BuildContext, "durationSec">,
): string {
  const props = testimonialSchema.parse(rawProps) as TestimonialProps;
  const loc = getLocale(ctx.locale as LocaleCode);
  const ctaText = props.cta || loc.ctas[props.ctaKey as keyof typeof loc.ctas];

  const fullCtx: BuildContext = { ...ctx, durationSec: TESTIMONIAL_DURATION_SEC };

  const quoteSize = scaleFor(ctx.format, { base: 72, short: 84, square: 64, wide: 78 });
  const personaSize = scaleFor(ctx.format, { base: 48, short: 56, square: 44, wide: 52 });
  const ctaSize = scaleFor(ctx.format, { base: 50, short: 60, square: 46, wide: 54 });
  const logoSize = scaleFor(ctx.format, { base: 80, short: 100, square: 80, wide: 90 });

  const cardStart = 4 / 30;
  const cardDur = TESTIMONIAL_DURATION_SEC - cardStart;
  const ctaStart = 180 / 30;
  const ctaDur = 70 / 30;

  const bg = backgroundGradient(fullCtx, "bg", { variant: "dark", animated: true });
  const logoFrag = brandLogo({ id: "tpl-logo", size: logoSize, color: "white" });
  const ctaFrag = cta({ id: "tpl-cta", text: ctaText, startSec: ctaStart, durationSec: ctaDur, size: ctaSize });

  const initials = props.persona
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const stars = Array.from({ length: 5 })
    .map((_, i) => `<span style="font-size:${Math.round(personaSize * 0.7)}px;color:${i < props.rating ? colors.accent : "rgba(255,255,255,0.2)"};">★</span>`)
    .join("");

  const cardInner = `
<div style="font-weight:900;font-size:${Math.round(quoteSize * 1.4)}px;color:${colors.primary};line-height:0.6;">"</div>
<div style="font-weight:700;font-size:${quoteSize}px;color:white;line-height:1.18;margin-top:12px;text-wrap:balance;">${escapeHtml(props.quote)}</div>
<div style="display:flex;gap:6px;margin-top:32px;">${stars}</div>
<div style="display:flex;align-items:center;gap:20px;margin-top:24px;">
  <div style="width:${personaSize * 1.6}px;height:${personaSize * 1.6}px;border-radius:9999px;background:linear-gradient(135deg, ${colors.primary}, ${colors.secondary});display:flex;align-items:center;justify-content:center;font-weight:800;font-size:${Math.round(personaSize * 0.7)}px;color:white;">${escapeHtml(initials)}</div>
  <div>
    <div style="font-weight:800;font-size:${personaSize}px;color:white;">${escapeHtml(props.persona)}</div>
    ${props.role ? `<div style="font-weight:500;font-size:${Math.round(personaSize * 0.55)}px;color:${colors.muted};">${escapeHtml(props.role)}</div>` : ""}
  </div>
</div>
${props.result ? `<div style="margin-top:28px;display:inline-block;background:${colors.accent}22;border:1px solid ${colors.accent};border-radius:9999px;padding:${Math.round(personaSize * 0.3)}px ${Math.round(personaSize * 0.6)}px;color:${colors.accent};font-weight:800;font-size:${Math.round(personaSize * 0.6)}px;">${escapeHtml(props.result)}</div>` : ""}
`;

  const cardFrag = card({ id: "tpl-card", childrenHtml: cardInner, startSec: cardStart, padding: 64, variant: "filled" });

  const innerHtml = `
<div style="display:flex;justify-content:flex-start;">${logoFrag.html}</div>
<div style="flex:1;display:flex;flex-direction:column;justify-content:center;">
  <div id="tpl-card-clip" class="clip" data-start="${cardStart}" data-duration="${cardDur}" data-track-index="0">${cardFrag.html}</div>
</div>
<div id="tpl-cta-clip" class="clip" data-start="${ctaStart}" data-duration="${ctaDur}" data-track-index="1" style="display:flex;justify-content:center;">${ctaFrag.html}</div>
`;

  const safe = safeArea({ id: "tpl-safe", format: ctx.format, childrenHtml: innerHtml });

  return assemble({
    templateId: "TestimonialCard",
    ctx: fullCtx,
    background: bg,
    contentFragments: [logoFrag, cardFrag, ctaFrag],
    safeAreaInnerHtml: innerHtml,
    safeArea: safe,
  });
}
